export interface OperatorCardDetails {
  cardNumber?: string;
  expiryMonth?: string;
  expiryYear?: string;
  cvc?: string;
  nameOnCard?: string | null;
  fields?: Record<string, string> | null;
}

export type CardFieldLabel =
  | 'Number'
  | 'Expiry'
  | 'CVC'
  | 'Name'
  | 'Billing Address'
  | 'City'
  | 'Postcode'
  | 'Country';

export interface CardFieldRow {
  label: CardFieldLabel;
  value: string;
}

/**
 * Rows shown to the operator for a card. Agent (VCC) cards arrive as top-level
 * fields; user cards arrive as the vaulted `fields` map written by the
 * add-card form (`cardNumber`, `expiryDate`, `cvv`, `cardholderName`, and the
 * optional `billing*` address fields). Billing rows are only shown for user
 * cards; a row with an empty value means the vault item lacks that field.
 */
export function buildCardFieldRows(card: OperatorCardDetails): CardFieldRow[] {
  const f = card.fields;
  const v = f ?? {};
  const expiry =
    card.expiryMonth && card.expiryYear
      ? `${card.expiryMonth}/${card.expiryYear}`
      : (v.expiryDate ?? v.expiry ?? '');
  const rows: CardFieldRow[] = [
    { label: 'Number', value: card.cardNumber ?? v.cardNumber ?? '' },
    { label: 'Expiry', value: expiry },
    { label: 'CVC', value: card.cvc ?? v.cvv ?? '' },
    { label: 'Name', value: card.nameOnCard ?? v.cardholderName ?? '' },
  ];
  if (!f) return rows;
  return rows.concat([
    {
      label: 'Billing Address',
      value: [f.billingAddressLine1, f.billingAddressLine2]
        .filter(Boolean)
        .join(', '),
    },
    { label: 'City', value: f.billingCity ?? '' },
    { label: 'Postcode', value: f.billingPostcode ?? '' },
    { label: 'Country', value: f.billingCountry ?? '' },
  ]);
}
